import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import VideoCard from "@/components/feature/VideoCard";
import Seo from "@/components/base/Seo";
import { videos, videoCategories } from "@/mocks/videos";
import { newsList } from "@/mocks/news";

const faqs = [
  {
    q: "瓜趣视频需要安装插件或客户端吗？",
    a: "完全不需要。瓜趣视频采用网页直连技术，无需安装任何插件或客户端，打开浏览器即可即点即看，秒开播放。",
  },
  {
    q: "手机端观看体验如何？",
    a: "我们的页面针对手机做了深度适配，横竖屏都能流畅观看，配合极速缓冲技术，即使在移动网络下也能保持高清流畅的画质。",
  },
  {
    q: "视频清晰度如何，是否卡顿？",
    a: "平台优先提供高清画质，并针对不同网络环境自动切换清晰度档位，配合极速缓冲与智能预加载，尽量降低卡顿概率。",
  },
  {
    q: "如何快速找到我感兴趣的视频？",
    a: "你可以在首页或视频库页通过搜索框输入关键词，也可以按娱乐综艺、热剧剪辑、搞笑日常等分类浏览，快速定位想看的内容。",
  },
  {
    q: "想看的内容没找到怎么办？",
    a: "欢迎通过联系我们页面向我们投稿或留言，我们会根据用户反馈持续扩充内容库，让更多好内容能被轻松找到。",
  },
];

export default function Home() {
  const [keyword, setKeyword] = useState("");
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const navigate = useNavigate();

  const hotVideos = videos.filter((v) => v.hot);
  const latestVideos = videos.filter((v) => !v.hot);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(keyword.trim() ? `/library?q=${encodeURIComponent(keyword.trim())}` : "/library");
  };

  return (
    <PageShell>
      <Seo
        title="瓜趣视频 - 娱乐八卦资讯与热门视频在线观看"
        description="瓜趣视频专注提供秒开播放体验，无需安装任何插件，网页直连即点即看。高清流畅画质配合极速缓冲技术，手机端完美适配，随时随地畅享娱乐八卦、综艺热剧、搞笑日常等最新热门视频内容。"
        keywords="瓜趣视频,吃瓜视频在线,娱乐八卦资讯,热门视频在线观看,秒开播放"
        canonical="https://gagplay6.github.io/"
        ogImage="https://readdy.ai/api/search-image?query=Playful%20watermelon%20and%20gossip%20news%20illustration%20with%20bright%20coral%20orange%20and%20fresh%20green%20colors%2C%20flat%20modern%20vector%20style%2C%20entertainment%20vibe&width=1200&height=630&seq=og-guqu&orientation=landscape"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "瓜趣视频 - 首页",
            url: "https://gagplay6.github.io/",
            description: "娱乐八卦资讯与热门视频在线观看聚合首页",
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]}
      />

      {/* Hero */}
      <section className="relative flex min-h-[440px] items-center justify-center overflow-hidden md:min-h-[560px]">
        <img
          src="https://readdy.ai/api/search-image?query=Playful%20watermelon%20gossip%20entertainment%20illustration%20with%20flat%20vector%20style%2C%20bright%20coral%20orange%20fresh%20green%20and%20warm%20cream%20colors%2C%20floating%20play%20buttons%20and%20confetti%20particles%2C%20vibrant%20energetic%20modern%20digital%20art%2C%20soft%20dark%20overlay%20friendly&width=1600&height=900&seq=hero-guqu&orientation=landscape"
          alt="瓜趣视频首屏背景：娱乐八卦与热门视频主题插画"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        <div className="relative z-10 w-full px-4 py-16 text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-background-50/15 px-4 py-1.5 text-sm font-medium text-background-50 backdrop-blur">
            <i className="ri-flashlight-fill text-secondary-300" aria-hidden="true"></i>
            秒开播放 · 无需安装 · 高清流畅
          </span>
          <h1 className="font-heading mx-auto mt-6 max-w-3xl text-3xl leading-tight text-background-50 md:text-5xl">
            吃瓜视频在线，热门内容一键畅看
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-background-100 md:text-lg">
            娱乐八卦、综艺热剧、搞笑日常尽在掌握。网页直连即点即看，手机端完美适配，随时随地畅享最新热门视频。
          </p>

          <form
            onSubmit={handleSearch}
            className="mx-auto mt-8 flex w-full max-w-xl items-center overflow-hidden rounded-full border border-background-50/30 bg-background-50/95 p-1.5 shadow-lg"
            role="search"
          >
            <i className="ri-search-line pl-3 text-lg text-foreground-400" aria-hidden="true"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索你想看的热门视频..."
              aria-label="搜索视频"
              className="w-full bg-transparent px-3 py-2.5 text-sm outline-none"
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              立即搜索
            </button>
          </form>

          <div className="mx-auto mt-8 flex flex-wrap items-center justify-center gap-6 text-background-100">
            {[
              { num: "3000+", label: "热门视频" },
              { num: "6+", label: "内容分类" },
              { num: "99%", label: "秒开成功率" },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <div className="font-heading text-2xl text-secondary-300">{s.num}</div>
                <div className="mt-0.5 text-sm">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 分类 */}
      <section className="container-x py-10" aria-labelledby="cat-title">
        <div className="flex items-center justify-between">
          <h2 id="cat-title" className="section-title text-foreground-950">
            按分类逛一逛
          </h2>
          <Link
            to="/library"
            className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            查看全部
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          {videoCategories
            .filter((c) => c !== "全部")
            .map((cat) => (
              <Link
                key={cat}
                to={`/library?category=${encodeURIComponent(cat)}`}
                className="chip bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
              >
                {cat}
              </Link>
            ))}
        </div>
      </section>

      {/* 热门视频 */}
      <section className="bg-background-100 py-10" aria-labelledby="hot-title">
        <div className="container-x">
          <div className="flex items-center gap-2">
            <i className="ri-fire-fill text-2xl text-primary-500" aria-hidden="true"></i>
            <h2 id="hot-title" className="section-title text-foreground-950">
              本周热门视频
            </h2>
          </div>
          <p className="mt-2 text-sm text-foreground-600">
            大家都在看的高能内容，高清流畅秒开，手机端同样过瘾。
          </p>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {hotVideos.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        </div>
      </section>

      {/* 最新视频 */}
      <section className="container-x py-10" aria-labelledby="new-title">
        <div className="flex items-center justify-between">
          <h2 id="new-title" className="section-title text-foreground-950">
            最新上架
          </h2>
          <Link
            to="/library"
            className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            进入视频库
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {latestVideos.map((video) => (
            <VideoCard key={video.id} video={video} />
          ))}
        </div>
      </section>

      {/* 八卦资讯 */}
      <section className="bg-accent-100/60 py-10" aria-labelledby="news-title">
        <div className="container-x grid gap-8 lg:grid-cols-3">
          <div>
            <span className="chip bg-accent-500 text-background-50">八卦资讯</span>
            <h2 id="news-title" className="section-title mt-3 text-foreground-950">
              今日吃瓜热点
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              综艺动态、热剧追踪、时尚八卦与演出现场，最热门的娱乐资讯一次看个够。
            </p>
            <Link to="/library" className="btn-secondary mt-5">
              查看更多内容
            </Link>
          </div>
          <ol className="space-y-4 lg:col-span-2">
            {newsList.map((news, idx) => (
              <li
                key={news.id}
                className="flex items-start gap-4 rounded-lg border border-background-200/70 bg-background-50 p-4"
              >
                <span className="font-heading flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-600">
                  {idx + 1}
                </span>
                <div className="min-w-0">
                  <h3 className="text-sm font-bold leading-snug text-foreground-950">{news.title}</h3>
                  <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-600">
                    {news.summary}
                  </p>
                  <div className="mt-2 flex items-center gap-3 text-xs text-foreground-500">
                    <span className="chip bg-secondary-100 px-2 py-0.5 text-secondary-900">{news.tag}</span>
                    <span>{news.date}</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line" aria-hidden="true"></i>
                      {news.reads}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="container-x py-10" aria-labelledby="faq-title">
        <div className="mx-auto max-w-3xl">
          <h2 id="faq-title" className="section-title text-center text-foreground-950">
            常见问题解答
          </h2>
          <p className="mt-2 text-center text-sm text-foreground-600">
            关于观看体验的常见疑问，这里都有答案。
          </p>
          <div className="mt-6 space-y-3">
            {faqs.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div key={faq.q} className="card-surface overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    aria-expanded={isOpen}
                    className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left cursor-pointer"
                  >
                    <span className="text-sm font-semibold text-foreground-950">{faq.q}</span>
                    <i
                      className={`${isOpen ? "ri-subtract-line" : "ri-add-line"} shrink-0 text-primary-600`}
                      aria-hidden="true"
                    ></i>
                  </button>
                  {isOpen && (
                    <p className="border-t border-background-200/70 px-5 py-4 text-sm leading-relaxed text-foreground-600">
                      {faq.a}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-x pb-12">
        <div className="relative overflow-hidden rounded-lg bg-primary-600 px-6 py-10 text-center md:py-12">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-500 to-primary-700"></div>
          <div className="relative z-10">
            <h2 className="font-heading text-2xl text-background-50 md:text-3xl">
              好内容别错过，现在就去视频库逛逛
            </h2>
            <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-background-100">
              高清流畅、极速缓冲、手机完美适配，无需安装即点即看。
            </p>
            <Link
              to="/library"
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-6 py-3 text-sm font-bold text-primary-700 transition-colors hover:bg-secondary-100 cursor-pointer"
            >
              <i className="ri-play-circle-fill text-lg" aria-hidden="true"></i>
              开始观看
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}